import { ArrowRightIcon, CheckCircleIcon, ClockIcon, SparkleIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";

import type { AiPack } from "@/client/services/ai-credit/api";
import { useAccountCurrency } from "@/client/services/currency/currency";

type Props = { pack: AiPack; index: number; onBuy: (pack: AiPack) => void };

export const PackCard = ({ pack, index, onBuy }: Props) => {
  const { format } = useAccountCurrency();
  const price = format(pack.price);

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.2), duration: 0.28 }}
      className={cn(
        "relative flex min-h-[280px] flex-col rounded-2xl border bg-white p-5 transition duration-200",
        "hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(30,27,75,0.08)]",
        pack.isRecommended ? "border-indigo-400 ring-2 ring-indigo-100" : "border-slate-200",
      )}
    >
      {pack.isRecommended && (
        <span className="absolute right-3 top-3 rounded-full bg-emerald-500 px-2.5 py-1 text-[8px] font-bold uppercase tracking-[0.1em] text-white">
          Recommandé
        </span>
      )}

      <span className="grid size-9 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
        <SparkleIcon size={18} weight="duotone" />
      </span>
      <p className="mt-4 text-[9px] font-bold uppercase tracking-[0.13em] text-indigo-600">
        Pack IA
      </p>
      <p className="mt-1 flex items-baseline gap-1.5 text-[#071451]">
        <span className="text-[28px] font-bold tabular-nums tracking-tight">
          {pack.aiPoints.toLocaleString("fr-FR")}
        </span>
        <span className="text-[11px] font-semibold text-slate-500">crédits</span>
      </p>
      <p className="mt-1 text-sm font-bold text-[#071451]">{price}</p>

      <div className="mt-4 space-y-2 text-[10px] leading-4 text-slate-500">
        <p className="flex items-center gap-2">
          <ClockIcon size={14} weight="duotone" className="shrink-0 text-indigo-600" />
          Valable {pack.validity} jour{pack.validity > 1 ? "s" : ""}
        </p>
        {pack.description && (
          <p className="flex items-start gap-2">
            <CheckCircleIcon size={14} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" />
            <span>{pack.description}</span>
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => onBuy(pack)}
        className={cn(
          "mt-auto inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl px-4 text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
          pack.isRecommended
            ? "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 text-white shadow-sm hover:-translate-y-0.5"
            : "border border-indigo-200 bg-indigo-50/50 text-indigo-800 hover:border-indigo-400 hover:bg-indigo-50",
        )}
      >
        Choisir ce pack <ArrowRightIcon size={15} weight="bold" />
      </button>
    </motion.article>
  );
};
